/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import classNames from 'classnames';
import { I18n } from '@coze-arch/i18n';
import { Tooltip } from '@coze-arch/coze-design';

import styles from './index.module.less';

export interface OntologyUsageChipsProps {
  names: string[];
  className?: string;
  onLinkOntology?: () => void;
}

const MAX_VISIBLE = 2;

export const OntologyUsageChips: FC<OntologyUsageChipsProps> = ({
  names,
  className,
  onLinkOntology,
}) => {
  const visible = names.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(0, names.length - MAX_VISIBLE);
  const hiddenNames = names.slice(MAX_VISIBLE);

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.label}>{I18n.t('abr_home_ontology_usage')}</div>
      <div className={styles.chips}>
        {names.length === 0 ? (
          <span className={styles.empty}>
            {I18n.t('abr_home_ontology_not_configured')}
            {onLinkOntology ? (
              <button
                type="button"
                className={styles.link}
                onClick={e => {
                  e.stopPropagation();
                  onLinkOntology();
                }}
              >
                {I18n.t('abr_home_ontology_link')}
              </button>
            ) : null}
          </span>
        ) : (
          <>
            {visible.map(name => (
              <span key={name} className={styles.chip}>
                {name}
              </span>
            ))}
            {hiddenCount > 0 ? (
              <Tooltip content={hiddenNames.join('、')}>
                <span className={classNames(styles.chip, styles.chipMore)}>
                  +{hiddenCount}
                </span>
              </Tooltip>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
